<template>
    <div id="admin-reports">
        <AppBreadCrumb :paths="[{ title: $t('report.detailReport') }]" :select="true">
            <select id="time-select" v-model="customDateFilter">
                <option value="today">{{ $t("time.today") }}</option>
                <option value="yesterday">{{ $t("time.yesterday") }}</option>
                <option value="current-week">
                    {{ $t("time.currentWeek") }}
                </option>
                <option value="last-week">{{ $t("time.lastWeek") }}</option>
                <option value="current-month">
                    {{ $t("time.currentMonth") }}
                </option>
                <option v-for="(m, i) in getLast11Months()" :key="i" :value="m.date">
                    {{ m.name }}
                </option>
                <option value="custom">
                    {{ $t("time.custom") }}
                </option>
            </select>
        </AppBreadCrumb>

        <div class="report-container">
            <ReportFilterTool v-model="filters" />
            <div class="report-content">
                <ReportMenu :loading="loading" :update-query-params="updateQueryParams" :current-tab="currentTab" />
                <div class="chart-container">
                    <div class="report-page-container">
                        <SitesReport :filters="filters" v-if="currentTab === ReportTabs.Sites" />
                        <AgentReport :filters="filters" v-if="currentTab === ReportTabs.AgentCount" />
                        <PaidReport :filters="filters" v-if="currentTab === ReportTabs.Payments" />
                        <PlanReport :filters="filters" v-if="currentTab === ReportTabs.Plan" />
                        <CustomerCandidate :filters="filters" v-if="currentTab === ReportTabs.CustomerCandidate" />
                        <BalanceReport :filters="filters" v-if="currentTab === ReportTabs.Balance" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import AppBreadCrumb from "../../components/common/AppBreadCrumb";
import moment from "moment";
import { REPORT_API } from "../../api/statistics";
import * as echarts from "echarts";
import { createComponent, plugin } from "echarts-for-vue";
import { log2 } from "../../utils/log2";
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import ReportMenu from "./common/ReportMenu.vue";
import ReportFilterTool from "./common/ReportFilterTool.vue";
import ReportTabs from "./common/ReportTabs";
import TimeUtils from "../../utils/TimeUtils";
import SitesReport from "./site/SitesReport.vue";
import AgentReport from "./agent/AgentReport.vue";
import PaidReport from "./paid/PaidReport.vue";
import CustomerCandidate from "./customer-candidate/CustomerCandidate.vue";
import PlanReport from "./plan/PlanReport.vue";
import BalanceReport from "./balance/BalanceReport.vue";

Vue.use(plugin, { echarts });

const defaultStartDate = moment().startOf("day").utc().toISOString();
const defaultEndDate = moment().endOf("day").utc().toISOString();
const log = log2("Reports");

export default {
    name: "Reports",
    components: {
        CustomerCandidate,
        ECharts: createComponent({ echarts }),
        ReportTabs,
        AppBreadCrumb,
        ReportFilterTool,
        ReportMenu,
        SitesReport,
        AgentReport,
        PaidReport,
        PlanReport,
        BalanceReport
    },
    data() {
        const startDate = this.$route.query.allTime
            ? null
            : this.$route.query.startDate
            ? moment(this.$route.query.startDate).toISOString()
            : defaultStartDate;
        const endDate = this.$route.query.allTime
            ? null
            : this.$route.query.endDate
            ? moment(this.$route.query.endDate).toISOString()
            : defaultEndDate;

        return {
            ReportTabs,
            currentTab: this.$route.query.report || ReportTabs.Sites,
            maxDate: moment().utc().toISOString(),
            loading: true,
            calendarTitle: "",
            customDateFilter: this.$route.query.customDate || "today",
            filters: {
                startDate,
                endDate,
                users: this.$route.query.selectedUsers ? this.$route.query.selectedUsers.split(",") : [],
            },
        };
    },
    computed: {
        ...mapState({
            language: (state) => state.language.language,
            isDarkMode: (state) => state.user.darkMode,
            users: (state) => state.user.users.data,
        }),
        moment,
        minDate() {
            return this.filters.startDate ? moment().subtract(45, "day").utc().toISOString() : null;
        },
    },
    methods: {
        ...mapActions({
            listChannels: "channel/listChannels",
            setRandomReportCacheKey: "app/setRandomReportCacheKey",
        }),
        fetchStats() {
            log("fetchStats", this.filters);
            this.loading = true;
            REPORT_API.calculateStats(this.filters)
                .then((data) => {
                    this.stats = data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getLast11Months() {
            return TimeUtils.getLast11Months();
        },
        getFormatedDate(filter) {
            log("getFormatedDate", filter);
            let startDate = null;
            let endDate = null;
            if (filter === "today") {
                startDate = moment().startOf("day").utc().toISOString();
                endDate = moment().endOf("day").utc().toISOString();
            } else if (filter === "yesterday") {
                startDate = moment().subtract(1, "days").startOf("day").utc().toISOString();
                endDate = moment().subtract(1, "days").endOf("day").utc().toISOString();
            } else if (filter === "current-week") {
                startDate = moment().startOf("week").startOf("day").utc().toISOString();
                endDate = moment().endOf("week").endOf("day").utc().toISOString();
            } else if (filter === "last-week") {
                startDate = moment().subtract(1, "week").startOf("week").startOf("day").utc().toISOString();
                endDate = moment().subtract(1, "week").endOf("week").endOf("day").utc().toISOString();
            } else if (filter === "current-month") {
                startDate = moment().startOf("month").startOf("day").utc().toISOString();
                endDate = moment().endOf("month").endOf("day").utc().toISOString();
            } else if (filter === "last-month") {
                startDate = moment().subtract(1, "month").startOf("month").startOf("day").utc().toISOString();
                endDate = moment().subtract(1, "month").endOf("month").endOf("day").utc().toISOString();
            } else if (typeof filter === "object") {
                startDate = filter.start;
                endDate = filter.end;
            } else {
                startDate = this.filters.startDate;
                endDate = this.filters.endDate;
            }
            return {
                startDate,
                endDate,
            };
        },
        updateQueryParams(key, value) {
            if (key === "report") {
                this.currentTab = value;
                // if (value === 5) this.renderCalendar();
            }
            if (value === "" || value == null) {
                let query = Object.assign({}, this.$route.query);
                delete query[key];
                this.$router.replace({ query });
            } else {
                let { query } = this.$route;
                this.$router.replace({
                    query: { ...query, [key]: encodeURI(value) },
                });
            }
        },
        getStatistics: _.debounce(async function () {
            this.fetchStats();
        }, 200),
        queryBuilder() {
            const { startDate, endDate, users } = this.filters;
            if (this.customDateFilter !== "custom") {
                const date = this.getFormatedDate(this.customDateFilter);
                if (
                    !moment(date.startDate).isSame(moment(startDate).utc().toISOString()) ||
                    !moment(date.endDate).isSame(moment(endDate).utc().toISOString())
                ) {
                    this.customDateFilter = "custom";
                }
            }
            const queries = {
                report: this.currentTab,
                customDate: this.customDateFilter === "string" ? this.customDateFilter : "custom",
            };
            if (startDate) queries["startDate"] = startDate;
            if (endDate) queries["endDate"] = endDate;
            if (users?.length > 0) queries["selectedUsers"] = users.join(",");
            this.$router.replace({
                query: {
                    ...queries,
                },
            });
        },
    },
    mounted() {
        this.setRandomReportCacheKey();
        moment.locale(this.$i18n.locale);
        if (this.customDateFilter !== "custom") {
            const { startDate, endDate } = this.getFormatedDate(this.customDateFilter);
            this.filters.startDate = startDate;
            this.filters.endDate = endDate;
        }
        this.getStatistics();
    },

    watch: {
        language(newValue) {
            log("new language", newValue);
            this.getStatistics();
        },
        filters: {
            deep: true,
            handler(newVal, oldVal) {
                this.getStatistics();
                this.queryBuilder();
            },
        },
        customDateFilter(newVal, oldVal) {
            const { startDate, endDate } = this.getFormatedDate(newVal);
            this.filters.startDate = startDate;
            this.filters.endDate = endDate;
        },
        isDarkMode(newValue, oldValue) {},
    },
};
</script>

<style lang="scss" scoped>
.report-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    width: 100%;

    .report-content {
        display: flex;
        gap: 10px;

        .chart-container {
            width: calc(100vw - 430px);
            max-width: calc(100vw - 430px);
        }
    }
}

.new-theme {
    #admin-reports {
        .report-page-container {
            min-height: 500px !important;
            background-color: #ffffff;
            padding: 15px 20px;
            border-radius: 6px;
            box-shadow: 0 0 3px #00000080;
        }

        .report-page-container-overall {
            background-color: #ffffff;
            padding: 15px 20px;
            border-radius: 6px;
            max-width: calc(100vw - 400px);
        }

        #time-select {
            border: none;
            background-color: #f3f6f9;
            padding: 3px 10px;
            border-radius: 6px;
            font-size: 12px;
            font-family: Poppins;
            font-weight: bolder;
            color: #3699ff;
        }

        .md-chip {
            border-radius: 6px;
            font-weight: 600;
            height: 26px;
            line-height: 26px;
        }

        .pageButtons {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 30px;
            height: 30px;
            border-radius: 6px;
            background-color: #ffffff;
            font-size: 22px;
            border: 1px solid #6c6c6c;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
                background-color: #f3f6f9;
            }
        }
    }
}

.dark-mode {
    #admin-reports {
        .box {
            background-color: #1e1e2d;

            p {
                color: #ffffff !important;
            }
        }

        .box.yes-hover {
            &:hover {
                background-color: #333440;
            }
        }

        .report-page-container {
            background-color: #1e1e2d !important;
            color: #ffffff !important;
            min-height: 500px !important;

            h5 {
                color: #ffffff !important;
            }
        }
    }
}
</style>
<style lang="scss">
.dark-mode {
    #admin-reports {
        .form-control {
            background-color: #1e1e2d !important;
            color: #ffffff !important;
            border: transparent;
        }
    }
}
</style>
